import { createIntl } from 'react-intl'

import { isAfter, parse } from 'date-fns'
import * as _ from 'lodash-es'

import { formatCustomerPhoneNumber } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import {
  isNumber,
  isValidCurrencyAmount,
  isValidDate,
  isValidDateOfBirth,
  isValidDateRange,
  isValidEmailString,
  isValidInteger,
  isValidNameString,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/validationUtils'
import { getFormattedMessageSafe } from '@lyrahealth-inc/shared-app-logic/src/features/intl/utils/getFormattedMessageSafe'

import { sanitizeLyraCode } from './utils'
import { LYRA_CODE_LENGTH } from '../../common/constants/appConstants'

// shared-app-logic's `isValidDateOfBirth` returns a `MessageDescriptor`, whereas the redux-form
// error objects built below are rendered straight as React children — an object there throws.
// The `validate` functions in this file are plain functions with no access to `props.intl`, and
// pp is English-only (`App.tsx` mounts `IntlProvider locale='en-US'` with no catalog), so a
// module-level `createIntl` formats identically to the provider without threading `intl` through
// every caller. Revisit if pp ever ships a translation catalog.
const { formatMessage } = createIntl({ locale: 'en-US', defaultLocale: 'en-US' })

/**
 * `isValidDateOfBirth` with the message already rendered to a string.
 *
 * `mustBeOlderErrorMessage` / `mustBeYoungerErrorMessage` interpolate `{min}` / `{max}`, which
 * shared-app-logic leaves to the caller, so `ageConstraints` is passed through as the values.
 */
export const validateDateOfBirth = (
  value: string,
  ageConstraints: { min: number; max: number },
): { isValid: boolean; message: string | undefined } => {
  const { isValid, message } = isValidDateOfBirth(value, ageConstraints)
  return { isValid, message: getFormattedMessageSafe(message, formatMessage, ageConstraints) }
}

const requiredFields = ($$values: any, fields: any) => {
  const errors: Record<string, string | undefined> = {}
  let val
  for (const r of fields) {
    val = $$values.get(r.fieldName)
    if (
      // A boolean set to false is invalid.
      // TODO: This is incorrect and needs to be fixed, but some forms rely on it like the ClientEmailForm.
      (_.isBoolean(val) && !val) ||
      // An empty value is invalid (null, undefined and [])
      (!_.isBoolean(val) && _.isEmpty(val)) ||
      // And empty immutable object is invalid
      (typeof val.isEmpty === 'function' && val.isEmpty())
    ) {
      errors[r.fieldName] = r.errorText + ' is required'
    }
  }
  return errors
}

const requiredValidEmail = ($$values: any, field: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = $$values.get(field.fieldName)
  if (!value) {
    errors[field.fieldName] = field.errorText + ' is required'
  } else if (!isValidEmailString(value)) {
    errors[field.fieldName] = 'Invalid email format'
  }
  return errors
}

const optionalValidEmail = ($$values: any, field: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = $$values.get(field.fieldName)
  if (!value) {
    return {}
  } else if (!isValidEmailString(value)) {
    errors[field.fieldName] = 'Invalid email format'
  }
  return errors
}

const requiredValidDOB = ($$values: any, field: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = $$values.get(field.fieldName)

  if (!value) {
    errors[field.fieldName] = 'Date of birth is required'
  } else {
    const { isValid, message } = validateDateOfBirth(value, { min: 18, max: 150 })
    if (!isValid) {
      errors[field.fieldName] = message
    }
  }

  return errors
}

const requiredValidName = ($$values: any, field: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = $$values.get(field.fieldName)

  if (!value) {
    errors[field.fieldName] = field.errorText + ' is required'
  } else {
    if (!isValidNameString(value)) {
      errors[field.fieldName] = 'Invalid ' + field.errorText
    }
  }

  return errors
}

const requiredValidMinimumDOB = ($$values: any, field: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = $$values.get(field.fieldName)

  if (!value) {
    errors[field.fieldName] = 'Date of birth is required'
  } else {
    const { isValid, message } = validateDateOfBirth(value, { min: 1, max: 150 })
    if (!isValid) {
      errors[field.fieldName] = message
    }
  }

  return errors
}

const requiredValidPhone = ($$values: any, field: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = $$values.get(field.fieldName)

  if (!value) {
    errors[field.fieldName] = field.errorText + ' is required'
  } else {
    const enteredValue = value
    const formattedNumber = formatCustomerPhoneNumber(enteredValue)

    if (enteredValue !== formattedNumber) {
      errors[field.fieldName] = 'Invalid phone number'
    }
  }

  return errors
}

const requiredValidNumberOfLength = ($$values: any, field: any, length: any) => {
  const errors: Record<string, string | undefined> = {}
  let value = $$values.get(field.fieldName)
  if (value) {
    value = value.replace(/\D/g, '')
  }

  if (!value) {
    errors[field.fieldName] = field.errorText + ' is required'
  } else {
    const enteredValue = value
    if (!(enteredValue.length === length && isNumber(parseInt(enteredValue, 10)))) {
      errors[field.fieldName] = 'Invalid ' + field.errorText.toLowerCase()
    }
  }

  return errors
}

const requiredValidStringOfLength = ($$values: any, field: any, length: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = $$values.get(field.fieldName)

  if (!value) {
    errors[field.fieldName] = field.errorText + ' is required'
  } else {
    if (!(value.length === length) || typeof value !== 'string') {
      errors[field.fieldName] = 'Invalid ' + field.errorText.toLowerCase()
    }
  }

  return errors
}

/**
 * Checks that the given field value only contains numbers.
 *
 * @param {Immutable.Map} $$values The form values map.
 * @param {Object} field The field to be checked.
 *
 * @return {Object} An object containing the errors, if any.
 */
const requiredValidNumber = ($$values: any, field: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = $$values.get(field.fieldName)

  if (!value) {
    errors[field.fieldName] = field.errorText + ' is required'
  } else if (!isNumber(parseInt(value, 10))) {
    errors[field.fieldName] = 'Invalid ' + field.errorText.toLowerCase()
  }

  return errors
}

/**
 * Checks that a given field value is a valid integer.
 *
 * @param {Immutable.Map} $$values The form values map.
 * @param {Object} field The field to be checked.
 * @return {Object} An object containing the errors, if any.
 */
const requiredValidInteger = ($$values: any, field: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = $$values.get(field.fieldName)

  if (!value) {
    errors[field.fieldName] = field.errorText + ' is required'
  } else if (!isValidInteger(value)) {
    errors[field.fieldName] = 'Invalid ' + field.errorText.toLowerCase()
  }

  return errors
}

/**
 * Checks that a given field value is a valid currency amount (= a positive decimal number).
 *
 * @param {Immutable.Map} $$values The form values map.
 * @param {Object} field The field to be checked.
 * @return {Object} An object containing the errors, if any.
 */
const requiredValidCurrencyAmount = ($$values: any, field: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = $$values.get(field.fieldName)

  if (!value) {
    errors[field.fieldName] = field.errorText + ' is required'
  } else if (!isValidCurrencyAmount(value)) {
    errors[field.fieldName] = 'Invalid ' + field.errorText.toLowerCase()
  }

  return errors
}

// TODO: the `isValidDate` branch below has never fired — the function returns `{ isValid, message }`,
// which is always truthy; the condition should be `!isValidDate(value).isValid`. Pre-existing and
// deliberately left alone: the only consumer is the downloadCSV date range, and fixing it would start
// surfacing errors that form has never shown. Needs its own change with a product decision.
const requiredValidDate = ($$values: any, field: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = $$values.get(field.fieldName)

  if (!value) {
    errors[field.fieldName] = field.errorText + ' is required'
  } else if (!isValidDate(value)) {
    errors[field.fieldName] = 'Invalid ' + field.errorText.toLowerCase()
  }

  return errors
}

const requiredValidDateRange = ($$values: any, startField: any, endField: any) => {
  const errors: Record<string, string | undefined> = {}
  const startValue = $$values.get(startField.fieldName)
  const endValue = $$values.get(endField.fieldName)
  if (startValue && endValue && !isValidDateRange(startValue, endValue)) {
    errors[endField.fieldName] = 'Invalid date range. Start date is past the end date.'
  }

  return errors
}

const validLyraCode = ($$values: any, field: any, isRequired: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = sanitizeLyraCode($$values.get(field.fieldName))
  if (!value) {
    if (isRequired) errors[field.fieldName] = field.errorText + ' is required'
  } else {
    if (value.length < LYRA_CODE_LENGTH) {
      errors[field.fieldName] = 'Incomplete ' + field.errorText.toLowerCase()
    }
  }

  return errors
}

const requiredBooleanField = ($$values: any, field: any) => {
  const errors: Record<string, string | undefined> = {}
  const value = $$values.get(field.fieldName)
  if (_.isNil(value)) {
    errors[field.fieldName] = field.errorText + ' is required'
  } else if (!_.isBoolean(value)) {
    errors[field.fieldName] = 'Invalid format'
  }
  return errors
}

/**
 * Used for the visitDate field in RequestPayment
 * Payment can't be requested after the present date
 *
 * @param {Immutable.Map} $$values The form values map.
 * @param {Object} field The field to be checked.
 * @return {Object} An object containing the errors, if any.
 */
const validVisitDate = ($$values: any, field: any) => {
  const errors: Record<string, string | undefined> = {}
  const maxDate = new Date()
  const value = parse($$values.get(field.fieldName), 'MM/dd/yyyy', new Date())
  if (isAfter(value, maxDate)) {
    errors[field.fieldName] = `${field.errorText} must not be in the future`
  }
  return errors
}

export default {
  optionalValidEmail,
  requiredFields,
  requiredValidEmail,
  requiredValidDOB,
  requiredValidMinimumDOB,
  requiredValidPhone,
  requiredValidNumberOfLength,
  requiredValidStringOfLength,
  requiredValidNumber,
  requiredValidDate,
  requiredValidDateRange,
  requiredValidInteger,
  requiredValidCurrencyAmount,
  requiredValidName,
  validLyraCode,
  requiredBooleanField,
  validVisitDate,
}
